import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RequestDetailModal } from './RequestDetailModal';
import { formatDistanceToNow } from 'date-fns';
import { displayPhDate, displayPhTime } from '@/lib/phDate';
import { useTargetPondName } from './useTargetPondName';
import { Clock, User, FileText } from 'lucide-react';
import type { EditRequest, EditRequestListResponse } from '@/types/edit-request';
import { formatTableName, getTargetDetail } from '@/types/edit-request';

interface RequestQueueProps {
  farmId: string;
  status: 'pending' | 'approved' | 'rejected';
}

function RequestCard({ request, onOpen }: { request: EditRequest; onOpen: () => void }) {
  const pondName = useTargetPondName(request);
  const target = getTargetDetail(request);
  const product = target.product && target.unit ? `${target.product} (${target.unit})` : target.product;
  const context = [pondName, target.date, target.slot, product].filter(Boolean).join(' · ');
  const verb = request.status === 'pending' ? 'Review' : 'View';
  const label = [formatTableName(request.target_table), pondName, target.date, target.slot, product]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
            <span className="font-medium text-gray-900">
              {formatTableName(request.target_table)}
            </span>
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <User className="h-3 w-3" />
              {request.requested_by_name}
            </span>
          </div>

          {context && (
            <p className="text-sm font-medium text-gray-700 mb-2">{context}</p>
          )}

          {request.reason && (
            <p className="text-sm text-gray-600 mb-2 flex items-start gap-1">
              <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span className="italic">"{request.reason}"</span>
            </p>
          )}

          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Requested {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}
          </p>

          {request.status !== 'pending' && request.resolved_at && (
            <p className="text-xs text-gray-500 mt-1">
              {request.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
              {request.resolved_by_name} on{' '}
              {displayPhDate(new Date(request.resolved_at), { month: 'short', day: 'numeric' })},{' '}
              {displayPhTime(request.resolved_at)}
            </p>
          )}

          {request.status === 'rejected' && request.rejection_reason && (
            <p className="text-xs text-red-600 mt-1 truncate max-w-md">
              Reason: {request.rejection_reason}
            </p>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onOpen}
          aria-label={`${verb} request: ${label}`}
        >
          {verb}
        </Button>
      </div>
    </Card>
  );
}

export function RequestQueue({ farmId, status }: RequestQueueProps) {
  const [selectedRequest, setSelectedRequest] = useState<EditRequest | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['edit-requests', farmId, status],
    queryFn: () =>
      api.get<EditRequestListResponse>(`/api/v1/farms/${farmId}/edit-requests`, {
        params: { status },
      }),
    refetchInterval: status === 'pending' ? 30000 : false,
  });

  const requests = data?.data?.requests || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card className="text-center py-12">
        <p className="text-gray-500">No {status} requests</p>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {requests.map((request) => (
          <RequestCard
            key={request.id}
            request={request}
            onOpen={() => setSelectedRequest(request)}
          />
        ))}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onResolved={() => setSelectedRequest(null)}
        />
      )}
    </>
  );
}
